import type { Voice } from '@listening/api-contract/legacy-client-types';
import { PUBLIC_TOOL_WEB_MAX_LENGTH } from '@listening/api-contract';
import { ApiClient } from '@listening/api-contract/api-client-tag';
import { Effect, Predicate } from 'effect';
import { clientEnv } from '@/env';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@listening/frontend/components/radix/popover';
import { useMutation, useQuery, queryOptions } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import {
  ArrowUpRight,
  Download,
  Link,
  Refresh,
  RefreshDouble,
  Sparks,
} from 'iconoir-react';
import {
  NavArrowDown as NavArrowDownSolid,
  WarningCircle as WarningCircleSolid,
} from 'iconoir-react/solid';
import { type SubmitEvent, useEffect, useState } from 'react';
import {
  type AudioExportFormat,
  downloadBlob,
  exportAudioAs,
} from '@/lib/audio-format-export';
import { PublicToolAudioPlayer } from '@/components/astro/new-design-25/SEO/public-tool-audio-player';
import { PublicToolReadAlong } from '@/components/astro/new-design-25/SEO/public-tool-read-along';
import {
  navigateToPublicToolReadAlong,
  type PublicToolJobResult,
} from '@/lib/public-tool-job';
import { prewarmTurnstileToken } from '@/lib/turnstile';
import {
  catchPublicToolHttpError,
  takeTurnstileTokenEffect,
} from '@/lib/public-tool-errors';
import { capturePublicToolClientTiming } from '@/lib/public-tool-client-timing';
import { voiceLangToLangLabel } from '@/lib/utils';
import * as m from '@/paraglide/messages';
import { getLocale, localizeHref } from '@/paraglide/runtime';
import { eq } from '@/runtimes/effect-query';
import { callApiPromise } from '@/runtimes/get-runtime';
import { rootLogger } from '@/lib/root-logger';

const moduleLogger = rootLogger.child({ module: 'web-to-audio-converter' });

const EXPORT_FORMATS: ReadonlyArray<{
  value: AudioExportFormat;
  label: string;
}> = [
  { value: 'mp3', label: 'MP3' },
  { value: 'wav', label: 'WAV' },
  { value: 'ogg', label: 'OGG' },
  { value: 'flac', label: 'FLAC' },
  { value: 'm4a', label: 'M4A' },
  { value: 'aac', label: 'AAC' },
];

const SPEED_OPTIONS = [0.75, 1, 1.25, 1.5] as const;
type Speed = (typeof SPEED_OPTIONS)[number];

type WebToAudioResult = Extract<PublicToolJobResult, { tool: 'web-to-audio' }>;

// 5xx and undeclared transport failures share this copy; the endpoint's own
// InternalServerError message is not written for end users.
const SERVER_ERROR_COPY = 'Server error. Please try again shortly.';

function describeVoice(voice: Voice) {
  const gender =
    voice.gender === 'male'
      ? m.settings_voice_male()
      : m.settings_voice_female();
  const softness =
    voice.softness === 'soft'
      ? m.settings_voice_soft()
      : voice.softness === 'deep'
        ? m.settings_voice_deep()
        : m.settings_voice_moderate();
  const language =
    voice.lang === 'en' ? null : voiceLangToLangLabel(voice.lang);
  return [gender, softness, language].filter(Boolean).join(' · ');
}

function pickInitialVoice({
  preferredGender,
  preferredLang,
  voices,
}: {
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  voices: ReadonlyArray<Voice>;
}): Voice | undefined {
  const locale = getLocale();
  return (
    voices.find(
      (v) =>
        v.lang === (preferredLang ?? locale) &&
        (!preferredGender || v.gender === preferredGender),
    ) ??
    voices.find((v) => v.lang === (preferredLang ?? locale)) ??
    voices.find((v) => !preferredGender || v.gender === preferredGender) ??
    voices[0]
  );
}

function slugifyTitle(title: string | null, fallback: string): string {
  const base = (title ?? fallback)
    .toLowerCase()
    .replaceAll(/[^a-z0-9\s-]/g, '')
    .replaceAll(/\s+/g, '-')
    .replaceAll(/-+/g, '-')
    .replaceAll(/^-|-$/g, '')
    .slice(0, 60);
  return base || fallback;
}

export function WebToAudioConverter({
  heading = 'Web to audio',
  preferredGender,
  preferredLang,
  subtitle = 'Paste a link, pick a natural AI voice, and listen to the article as audio.',
}: {
  heading?: string | undefined;
  preferredGender?: Voice['gender'] | undefined;
  preferredLang?: Voice['lang'] | undefined;
  subtitle?: string | undefined;
}) {
  const voicesQuery = useQuery(
    queryOptions({
      queryKey: ['tts-tool', 'voices'],
      queryFn: () => callApiPromise((api) => api.Voices.ListVoices()),
      select: (response) => response.voices.filter((v) => v.is_free),
      staleTime: 1000 * 60 * 60 * 24,
    }),
  );
  const voices = voicesQuery.data ?? [];

  const [voiceOverride, setVoiceOverride] = useState<Voice['model_id']>();
  const voiceModelId =
    voiceOverride ??
    pickInitialVoice({ preferredGender, preferredLang, voices })?.model_id;
  const selectedVoice = voices.find((v) => v.model_id === voiceModelId);

  const navigate = useNavigate();
  const [url, setUrl] = useState('');
  const [speed, setSpeed] = useState<Speed>(1);
  const [format, setFormat] = useState<AudioExportFormat>('mp3');

  // Run the CAPTCHA while the user is pasting/typing the URL so the token
  // round-trip is off the submit critical path.
  useEffect(() => {
    if (url.trim().length < 8) return;
    prewarmTurnstileToken({ sitekey: clientEnv.turnstileSiteKey });
  }, [url]);

  const generate = useMutation({
    ...eq.mutationOptions({
      mutationFn: (payload: { url: string; voice: Voice['model_id'] }) =>
        Effect.gen(function* () {
          const api = yield* ApiClient;
          const submitStart = performance.now();
          const cf_turnstile_token = yield* takeTurnstileTokenEffect(
            clientEnv.turnstileSiteKey,
          );
          const turnstileDoneAt = performance.now();
          const job = yield* api.PublicTools.WebToAudio({
            payload: { ...payload, cf_turnstile_token },
          }).pipe(
            Effect.catchTag(
              'HttpClientError',
              catchPublicToolHttpError('web-to-audio'),
            ),
          );
          // Resolves as soon as the read-along is openable (first chunks still
          // synthesizing) instead of waiting for the full conversion.
          capturePublicToolClientTiming({
            tool: 'web-to-audio',
            turnstileMs: turnstileDoneAt - submitStart,
            createJobMs: performance.now() - turnstileDoneAt,
          });
          // Navigate immediately — the read-along page shows a reading state
          // while the article is extracted and the job materializes.
          yield* Effect.promise(() =>
            navigateToPublicToolReadAlong(
              navigate,
              job.job_id,
              { speed, voice: voiceModelId },
              { tool: 'web-to-audio', title: payload.url },
            ),
          );
          // The navigation unmounts this page, so nothing renders the resolved
          // value; it is typed as the completed result so the success-view
          // branches below still typecheck.
          return yield* Effect.succeed<WebToAudioResult | null>(null);
        }),
    }),
    onError: (error) => {
      if (Predicate.isTagged(error, 'EffectQueryDefect')) {
        moduleLogger.error(
          { defectCause: error.defectCause },
          '[Listening] web-to-audio defect',
        );
      }
    },
  });

  const download = useMutation({
    mutationFn: async (input: {
      url: string;
      format: AudioExportFormat;
      baseName: string;
      speed: Speed;
    }) => {
      const blob = await exportAudioAs(input.url, input.format, {
        playbackRate: input.speed,
      });
      const speedSuffix = input.speed === 1 ? '' : `-${input.speed}x`;
      downloadBlob(blob, `${input.baseName}${speedSuffix}.${input.format}`);
    },
  });

  const result = generate.data;
  // A defect (network/parse) carries no tag, so it lands in `OrElse`; `onError`
  // logs it. Never render its `.message` — that's `Cause.pretty(cause)`.
  const generateErrorMessage = generate.error
    ? generate.error.match({
        PublicToolCaptchaError: () => m.try_magic_error_captcha(),
        CaptchaError: () => m.try_magic_error_captcha(),
        // Server-authored extraction/validation copy.
        BadRequestError: (error) => error.message,
        RateLimitedError: (error) =>
          error.scope === 'global'
            ? error.message
            : m.try_magic_error_rate_limit(),
        PublicToolRateLimitedError: () => m.try_magic_error_rate_limit(),
        PublicToolUnavailableError: () =>
          'Web to audio is temporarily unavailable. Please try again shortly.',
        PublicToolRequestFailedError: () => SERVER_ERROR_COPY,
        InternalServerError: () => SERVER_ERROR_COPY,
        OrElse: () => m.try_magic_error_unexpected(),
      })
    : null;
  // Only the extraction/validation failure has a useful fallback: the article
  // never made it to text, so the user can paste it themselves.
  const showPasteTextFallback = generate.error
    ? generate.error.match({
        BadRequestError: () => true,
        OrElse: () => false,
      })
    : false;
  const errorMessage =
    generateErrorMessage ??
    (download.isError ? m.tts_tool_download_error() : null);

  const canSubmit =
    url.trim().length > 0 && Boolean(voiceModelId) && !generate.isPending;

  const onSubmit = (event: SubmitEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canSubmit || !voiceModelId) return;
    generate.mutate({ url: url.trim(), voice: voiceModelId });
  };

  const resetTool = () => {
    setUrl('');
    generate.reset();
    download.reset();
  };

  const article = result?.article;
  const baseName = article
    ? `listening-${slugifyTitle(article.title, 'web-to-audio')}`
    : 'listening-web-to-audio';

  return (
    <section
      aria-labelledby='web-to-audio-tool-heading'
      className='mx-auto max-w-6xl px-4 pt-10 pb-16 sm:px-6 sm:pt-14 lg:px-8'
    >
      <div className='mb-8 text-center sm:mb-10'>
        <h1
          className='text-3xl font-semibold tracking-[-0.04em] text-navy-220 sm:text-5xl'
          id='web-to-audio-tool-heading'
        >
          {heading}
        </h1>
        <p className='mx-auto mt-3 max-w-2xl text-base leading-7 text-navy-220/65 sm:text-lg'>
          {subtitle}
        </p>
      </div>

      <div className='rounded-[28px] border border-navy-220/10 bg-white shadow-[0_24px_80px_rgba(15,23,42,0.10)]'>
        {!result && (
          <form className='p-6 sm:p-10' onSubmit={onSubmit}>
            <label
              className='block text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'
              htmlFor='web-to-audio-url-input'
            >
              Article URL
            </label>
            <div className='mt-3 flex items-center gap-2 rounded-2xl border border-navy-220/15 bg-navy-220/[0.02] px-4 py-3 transition focus-within:border-royal-50 focus-within:bg-white'>
              <Link
                aria-hidden='true'
                className='size-5 shrink-0 text-navy-220/50'
              />
              <input
                aria-label='Article URL'
                autoComplete='url'
                className='flex-1 border-none bg-transparent text-base text-navy-220 outline-hidden placeholder:text-navy-220/40 sm:text-lg'
                disabled={generate.isPending}
                id='web-to-audio-url-input'
                onChange={(event) => {
                  setUrl(event.target.value);
                  generate.reset();
                }}
                placeholder='https://example.com/some-article'
                type='url'
                value={url}
              />
            </div>
            <p className='mt-3 text-sm text-navy-220/55'>
              Works on articles, blog posts, papers, and most news pages. Up to{' '}
              {(PUBLIC_TOOL_WEB_MAX_LENGTH / 1000).toString()}K characters per
              request.
            </p>
          </form>
        )}

        {result && article && (
          <div className='p-6 sm:p-10'>
            {result.read_along ? (
              <PublicToolReadAlong
                audioUrl={result.tts.url}
                readAlong={result.read_along}
                speed={speed}
              />
            ) : (
              <PublicToolAudioPlayer
                key={result.tts.url}
                speed={speed}
                subtitle={article.site_name ?? 'Generated article audio'}
                title={baseName}
                url={result.tts.url}
              />
            )}
            <div className='mt-4 flex flex-wrap gap-2'>
              {clientEnv.publicToolDownloadsEnabled && (
                <button
                  className='inline-flex items-center gap-2 rounded-full border border-navy-220/15 bg-white px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5 disabled:cursor-not-allowed disabled:opacity-60'
                  disabled={download.isPending}
                  onClick={() => {
                    download.mutate({
                      url: result.tts.url,
                      format,
                      baseName,
                      speed,
                    });
                  }}
                  type='button'
                >
                  {download.isPending ? (
                    <RefreshDouble
                      aria-hidden='true'
                      className='size-4 animate-spin'
                    />
                  ) : (
                    <Download aria-hidden='true' className='size-4' />
                  )}
                  Download {format.toUpperCase()}
                </button>
              )}
              <button
                className='inline-flex items-center gap-2 rounded-full border border-navy-220/15 bg-white px-4 py-2 text-sm font-medium text-navy-220 transition hover:bg-navy-220/5'
                onClick={resetTool}
                type='button'
              >
                <Refresh aria-hidden='true' className='size-4' />
                New URL
              </button>
            </div>
            {article.char_count >= PUBLIC_TOOL_WEB_MAX_LENGTH && (
              <UpgradePromo article={article} />
            )}
          </div>
        )}

        {errorMessage && (
          <div
            className='border-t border-red-500/15 bg-red-50/60 px-6 py-4 sm:px-10'
            role='alert'
          >
            <div className='flex items-start gap-2 text-sm font-medium text-red-700'>
              <WarningCircleSolid
                aria-hidden='true'
                className='mt-0.5 size-4 shrink-0'
              />
              <span>{errorMessage}</span>
            </div>
            {showPasteTextFallback && (
              <p className='mt-2 pl-6 text-sm text-red-700/80'>
                You can also{' '}
                <a
                  className='font-semibold underline'
                  href={localizeHref('/text-to-speech')}
                >
                  paste the text into our text-to-speech converter
                </a>{' '}
                instead.
              </p>
            )}
          </div>
        )}

        <div className='flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-navy-220/10 px-6 py-5 sm:px-10'>
          {!result && (
            <VoicePicker
              disabled={voicesQuery.isLoading || generate.isPending}
              onSelect={(modelId) => {
                setVoiceOverride(modelId);
              }}
              selectedVoice={selectedVoice}
              voices={voices}
            />
          )}
          <SpeedSelector
            disabled={generate.isPending || download.isPending}
            onChange={setSpeed}
            speed={speed}
          />
          {clientEnv.publicToolDownloadsEnabled && (
            <FormatSelector
              disabled={generate.isPending || download.isPending}
              format={format}
              onChange={setFormat}
            />
          )}
        </div>

        {!result && (
          <div className='border-t border-navy-220/10 p-6 sm:p-10'>
            <button
              className='flex w-full items-center justify-center gap-3 rounded-2xl bg-royal-50 px-6 py-5 text-lg font-semibold text-white transition hover:bg-navy-220 disabled:cursor-not-allowed disabled:opacity-60'
              disabled={!canSubmit}
              onClick={(event) => {
                event.preventDefault();
                if (!canSubmit || !voiceModelId) return;
                generate.mutate({ url: url.trim(), voice: voiceModelId });
              }}
              type='button'
            >
              {generate.isPending && (
                <RefreshDouble
                  aria-hidden='true'
                  className='size-6 animate-spin'
                />
              )}
              <span>
                {generate.isPending ? 'Reading the page…' : 'Generate audio'}
              </span>
            </button>
          </div>
        )}
      </div>
    </section>
  );
}

function UpgradePromo({
  article,
}: {
  article: { char_count: number; url: string };
}) {
  return (
    <div className='mt-6 overflow-hidden rounded-2xl border border-azure-525/20 bg-linear-to-br from-azure-525/8 via-azure-525/5 to-white p-5 sm:p-6'>
      <div className='flex items-start gap-4'>
        <div className='flex size-11 shrink-0 items-center justify-center rounded-xl bg-azure-525/15 text-azure-525'>
          <Sparks aria-hidden='true' className='size-5' />
        </div>
        <div className='min-w-0 flex-1'>
          <p className='text-base font-semibold text-navy-220'>
            Want the rest of this article?
          </p>
          <p className='mt-1 text-sm leading-6 text-navy-220/70'>
            This preview is capped at {(article.char_count / 1000).toFixed(1)}K
            characters. Sign up to convert the whole article.
          </p>
          <div className='mt-4'>
            <a
              className='inline-flex items-center gap-2 rounded-full bg-royal-50 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-navy-220'
              href={localizeHref('/app/continue')}
              rel='noopener noreferrer'
              target='_blank'
            >
              Try now
              <ArrowUpRight aria-hidden='true' className='size-4' />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function VoicePicker({
  disabled,
  onSelect,
  selectedVoice,
  voices,
}: {
  disabled: boolean;
  onSelect: (modelId: Voice['model_id']) => void;
  selectedVoice: Voice | undefined;
  voices: ReadonlyArray<Voice>;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Voice'
        className='group inline-flex cursor-pointer items-center gap-2 rounded-full text-sm transition focus:outline-hidden disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled || voices.length === 0}
        type='button'
      >
        {selectedVoice ? (
          <>
            <img
              alt=''
              className='size-9 rounded-full object-cover ring-2 ring-white'
              height={36}
              src={selectedVoice.image_url}
              width={36}
            />
            <span className='font-medium text-navy-220'>
              {selectedVoice.name}{' '}
              <span className='text-navy-220/60'>
                · {describeVoice(selectedVoice)}
              </span>
            </span>
            <NavArrowDownSolid
              aria-hidden='true'
              className={`size-4 text-navy-220/60 transition-transform ${isOpen ? 'rotate-180' : ''}`}
            />
          </>
        ) : (
          <span className='text-navy-220/60'>{m.loading()}</span>
        )}
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='max-h-80 w-72 overflow-y-auto rounded-2xl border border-navy-220/10 bg-white p-2 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col gap-1' role='listbox'>
          {voices.map((voice) => {
            const isSelected = voice.model_id === selectedVoice?.model_id;
            return (
              <li key={voice.model_id}>
                <button
                  aria-selected={isSelected}
                  className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition hover:bg-slate-50 ${
                    isSelected ? 'bg-royal-50/5' : ''
                  }`}
                  onClick={() => {
                    onSelect(voice.model_id);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  <img
                    alt=''
                    className='size-9 rounded-full object-cover'
                    height={36}
                    src={voice.image_url}
                    width={36}
                  />
                  <span className='flex flex-col'>
                    <span className='font-medium text-navy-220'>
                      {voice.name}
                    </span>
                    <span className='text-sm text-navy-220/60'>
                      {describeVoice(voice)}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function SpeedSelector({
  disabled,
  onChange,
  speed,
}: {
  disabled: boolean;
  onChange: (speed: Speed) => void;
  speed: Speed;
}) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Speed'
        className='inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-220 transition hover:text-royal-50 disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled}
        type='button'
      >
        <span className='text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'>
          {m.tts_tool_speed_label()}
        </span>
        <span>{speed}×</span>
        <NavArrowDownSolid
          aria-hidden='true'
          className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-24 rounded-2xl border border-navy-220/10 bg-white p-1 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col' role='listbox'>
          {SPEED_OPTIONS.map((option) => {
            const isSelected = option === speed;
            return (
              <li key={option}>
                <button
                  aria-selected={isSelected}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                    isSelected ? 'font-semibold text-royal-50' : 'text-navy-220'
                  }`}
                  onClick={() => {
                    onChange(option);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  {option}×
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}

function FormatSelector({
  disabled,
  format,
  onChange,
}: {
  disabled: boolean;
  format: AudioExportFormat;
  onChange: (format: AudioExportFormat) => void;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const selected = EXPORT_FORMATS.find((option) => option.value === format);

  return (
    <Popover onOpenChange={setIsOpen} open={isOpen}>
      <PopoverTrigger
        aria-label='Audio format'
        className='inline-flex cursor-pointer items-center gap-2 text-sm font-medium text-navy-220 transition hover:text-royal-50 disabled:cursor-not-allowed disabled:opacity-60'
        disabled={disabled}
        type='button'
      >
        <span className='text-xs font-semibold tracking-[0.16em] text-navy-220/60 uppercase'>
          Format
        </span>
        <span>{selected?.label ?? format.toUpperCase()}</span>
        <NavArrowDownSolid
          aria-hidden='true'
          className={`size-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </PopoverTrigger>
      <PopoverContent
        align='start'
        className='w-28 rounded-2xl border border-navy-220/10 bg-white p-1 shadow-lg'
        sideOffset={8}
      >
        <ul className='flex flex-col' role='listbox'>
          {EXPORT_FORMATS.map((option) => {
            const isSelected = option.value === format;
            return (
              <li key={option.value}>
                <button
                  aria-selected={isSelected}
                  className={`w-full rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50 ${
                    isSelected ? 'font-semibold text-royal-50' : 'text-navy-220'
                  }`}
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                  role='option'
                  type='button'
                >
                  {option.label}
                </button>
              </li>
            );
          })}
        </ul>
      </PopoverContent>
    </Popover>
  );
}
